import { CameraGimbalSchema } from '../../../renderer/src/camera-gimbal-contract';
import { resolveTerrainExaggeration } from "../domain/terrain-height";
import { readCameraAngle } from '../../../renderer/src/camera-angle';
import { readPlaceTitle } from '../../../renderer/src/place-title';
import { readStoredExportRange } from '../../../renderer/src/export-range-contract';
import { readDepthOfField } from '../../../renderer/src/depth-of-field-contract';
import { readStoredPresentation } from '../../../renderer/src/presentation-contract';
import { readStoredTrackAppearance } from '../../../renderer/src/track-appearance-contract';
import { resolveUnitSystem } from "../domain/measurement-units";
import { resolveMapStyle } from "../domain/map-style";
import { normalizeCameraPlatform } from "../domain/camera-platform";
import { DEFAULT_CAMERA_DISTANCE_ID, isCameraDistanceId } from "../domain/camera-distance";
import { DEFAULT_ROUTE_LINE_STYLE_ID, isRouteLineStyleId } from "../domain/route-line-style";
import {
  DEFAULT_RENDER_DURATION_SECONDS, MAX_RENDER_DURATION_SECONDS, MIN_RENDER_DURATION_SECONDS,
} from "../domain/playback-pace";

/** Reads allowlisted presentation settings from the composition URL. */
export function readCompositionOptions(search: string) {
  const query = new URLSearchParams(search);
  const camera = query.get("camera") ?? "orbit";
  const distance = query.get("distance") ?? DEFAULT_CAMERA_DISTANCE_ID;
  const line = query.get("line") ?? DEFAULT_ROUTE_LINE_STYLE_ID;
  const duration = Number(query.get("duration"));
  const style = resolveMapStyle(query.get("map") ?? "heat");
  const presentation = readStoredPresentation(query.get("presentation"));
  const trackAppearance = readStoredTrackAppearance(query.get('trackAppearance'));
  const gimbal = query.get('gimbal');
  const cameraGimbal = gimbal === null ? undefined : CameraGimbalSchema.parse(gimbal);
  const cameraAngle = readCameraAngle(query.get('angle'));
  const depthOfField = readDepthOfField(query.get('depthOfField'));
  const placeTitle = readPlaceTitle(query.get('placeTitle'));
  const exportRange = readStoredExportRange(query.get('exportRange'));
  return {
    cameraStyleVersion: (query.get("cameraVersion") === "3" ? 3 : 4) as 3 | 4,
    ...(cameraGimbal === undefined ? {} : { cameraGimbal }),
    ...(exportRange ? { exportRange } : {}),
    ...(placeTitle ? { placeTitle } : {}),
    ...(depthOfField ? { depthOfField } : {}),
    ...(cameraAngle === undefined ? {} : { cameraAngle }),
    ...(trackAppearance ? { trackAppearance } : {}),
    ...(presentation ? { presentation } : {}),
    unitSystem: resolveUnitSystem(query.get("units")),
    mapStyleId: style.id,
    hidePlaces: query.get("hidePlaces") === "true",
    terrainExaggeration: resolveTerrainExaggeration(Number(query.get("terrain"))),
    cameraStyleId: normalizeCameraPlatform(camera) ?? "orbit" as const,
    cameraDistanceId: isCameraDistanceId(distance) ? distance : DEFAULT_CAMERA_DISTANCE_ID,
    lineStyleId: isRouteLineStyleId(line) ? line : DEFAULT_ROUTE_LINE_STYLE_ID,
    durationSeconds: Number.isInteger(duration)
      && duration >= MIN_RENDER_DURATION_SECONDS && duration <= MAX_RENDER_DURATION_SECONDS
      ? duration : DEFAULT_RENDER_DURATION_SECONDS,
  };
}
